import { Link } from 'react-router-dom';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { MARKETING_FAQ_SECTIONS } from '@/lib/marketing/faqStructure';
import { cn } from '@/lib/utils';
import { PageSeo } from '@/components/seo/PageSeo';
import { faqSeo } from '@/lib/seo/marketingSeo';
import { FaqJsonLd } from '@/components/seo/FaqJsonLd';

function FaqAnswer({ text }: { text: string }) {
  const paragraphs = text.split('\n\n').filter(Boolean);
  return (
    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

export default function FaqPage() {
  const { t, language } = useLanguage();

  const seo = faqSeo(language);

  return (
    <div className="min-h-screen bg-brand-surface-light text-foreground flex flex-col">
      <PageSeo title={seo.title} description={seo.description} path="/faq" />
      <FaqJsonLd />
      <MarketingNav />

      <main className="flex-1 container py-10 md:py-14">
        <header className="max-w-3xl mx-auto text-center space-y-4 mb-12 md:mb-16">
          <div className="flex justify-center">
            <HelpCircle className="h-10 w-10 text-brand-accent" aria-hidden />
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            {t('marketing.faqPage.title')}
          </h1>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            {t('marketing.faqPage.subtitle')}
          </p>
        </header>

        <div className="grid lg:grid-cols-[220px_1fr] gap-10 max-w-5xl mx-auto">
          <nav
            className="hidden lg:block sticky top-20 self-start space-y-1 text-sm"
            aria-label={t('marketing.faqPage.title')}
          >
            {MARKETING_FAQ_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#faq-${section.id}`}
                className="block py-1.5 px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                {t(section.titleKey)}
              </a>
            ))}
            <a
              href="#faq-contact"
              className="block py-1.5 px-2 rounded-md text-brand-accent font-medium hover:bg-brand-accent/10 transition-colors mt-4"
            >
              {t('marketing.faqPage.contactHint.cta')}
            </a>
          </nav>

          <div className="space-y-14 min-w-0">
            {MARKETING_FAQ_SECTIONS.map((section) => (
              <section
                key={section.id}
                id={`faq-${section.id}`}
                className="scroll-mt-24"
              >
                <h2 className="font-display font-bold text-xl md:text-2xl mb-3">
                  {t(section.titleKey)}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">{t(section.introKey)}</p>
                <Accordion type="multiple" className="w-full space-y-2">
                  {section.items.map((item, idx) => (
                    <AccordionItem
                      key={item.qKey}
                      value={`${section.id}-${idx}`}
                      className="border border-border/60 rounded-lg px-4 bg-card"
                    >
                      <AccordionTrigger className="text-left font-medium hover:no-underline py-4">
                        {t(item.qKey)}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <FaqAnswer text={t(item.aKey)} />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}

            <Card
              id="faq-contact"
              className={cn('scroll-mt-24 border-brand-accent/25 bg-card')}
            >
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center gap-2 text-brand-accent">
                  <MessageCircle className="h-5 w-5" aria-hidden />
                  <h2 className="font-display font-bold text-lg">
                    {t('marketing.faqPage.contactHint.title')}
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('marketing.faqPage.contactHint.body')}
                </p>
                <Button className="bg-brand-accent hover:bg-brand-accent/90" asChild>
                  <Link to="/contact">{t('marketing.faqPage.contactHint.cta')}</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
